/**
 * Fault Tree Dendrogram Component for IRP Workbook
 *
 * Visualizes the hierarchical structure of the fault tree with report mappings
 * Shows which nodes have reports mapped to them
 * Uses D3.js for rendering
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import {Download, LineWeight, SwapHoriz} from '@mui/icons-material';
import * as d3 from 'd3';
import {useIRPWorkbookStore, useIRPWorkbookStoreApi} from '../../../../../../stores/irp-workbook.store';
import EventSequence from '../../../../../../@types/event-sequence';
import {FaultTreeNode} from '../../../../../../@types/faultTreeNode';
import {FaultTreeNodeBadgeDTO} from '../../../../../../@types/integrated-risk-picture';

// Define types for our tree data with d3 hierarchy properties
interface TreeNode extends d3.HierarchyNode<FaultTreeNode & { mappedReportCount?: number; mappedReportIds?: number[] }> {
    _children?: TreeNode[]; // Hidden children when collapsed
}

// SVG path for Material Icons Visibility icon (24x24 viewBox)
// This will be scaled down to fit within the node visualization
const EYE_ICON_PATH = 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z';

/**
 * What the view needs from whatever is hosting it. The workbook supplies these from its store; a
 * shakedown, which has no workbook and nowhere to navigate to, supplies its own.
 *
 * The collapse state and transform are read through getters rather than passed as values, because
 * the drawing effect reads them without wanting to redraw when they change, which is exactly what
 * the store's non-reactive getState() gave it.
 */
export interface DendrogramHost {
    /** The model whose tree is drawn; the tree is read from events[0].faultTree. */
    eventSequenceData: EventSequence | null;
    /** Opens a node elsewhere. Absent when there is nowhere to go, which hides the eye icon. */
    onNavigateToNode?: (nodeId: number) => void;
    /** Null on the very first draw, which is what selects the default collapse depth. */
    getCollapsedNodeIds: () => Set<number> | null;
    setCollapsedNodeIds: (ids: Set<number>) => void;
    getTransform: () => { x: number; y: number; k: number } | null;
    setTransform: (transform: { x: number; y: number; k: number }) => void;
}

interface FaultTreeDendrogramProps {
    /** Badge counts per node from the BFF overview endpoint (Task 12). */
    nodeBadges?: FaultTreeNodeBadgeDTO[];
    /** When true, node badges are being fetched from the server. */
    isLoading?: boolean;
}

/**
 * The dendrogram itself, host-agnostic. Never touches the workbook store, so it can be drawn
 * outside one; {@link FaultTreeDendrogram} is the workbook's wrapper around it.
 */
export const FaultTreeDendrogramView: React.FC<FaultTreeDendrogramProps & DendrogramHost> = ({
    nodeBadges,
    isLoading = false,
    eventSequenceData,
    onNavigateToNode,
    getCollapsedNodeIds,
    setCollapsedNodeIds,
    getTransform,
    setTransform,
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const [updateTrigger, setUpdateTrigger] = useState(0); // Force re-render when nodes expand/collapse
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [orientation, setOrientation] = useState<'ltr' | 'rtl'>('ltr'); // Left-to-right or right-to-left
    const [scaleLineWidth, setScaleLineWidth] = useState(false); // Toggle for scaled line widths
    const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
    const rootNodeRef = useRef<TreeNode | null>(null); // Keep reference to root for updates
    const currentTransformRef = useRef<d3.ZoomTransform | null>(null); // Store current zoom/pan transform (within a mount)

    // Get data from store

    // ─── Download helpers ──────────────────────────────────────────────────────

    interface NodeDownloadRow {
        name: string;
        description: string;
        statementOfTruth: string;
        negativeInteractionCount: number;
        positiveInteractionCount: number;
    }

    const buildDownloadRows = (): NodeDownloadRow[] => {
        const faultTreeNodes = eventSequenceData?.events?.[0]?.faultTree?.faultTreeNodes ?? [];
        const badgeMap = new Map((nodeBadges ?? []).map(b => [b.nodeId, b]));
        return faultTreeNodes.map((node: FaultTreeNode): NodeDownloadRow => {
            const badge = badgeMap.get(node.id);
            return {
                name: node.name ?? '',
                description: node.description ?? '',
                statementOfTruth: node.statementOfTruth ?? '',
                negativeInteractionCount: badge?.negativeInteractionCount ?? 0,
                positiveInteractionCount: badge?.positiveInteractionCount ?? 0,
            };
        });
    };

    const triggerDownload = (content: string, filename: string, mimeType: string): void => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadCsv = (): void => {
        const rows = buildDownloadRows();
        const escape = (v: string): string => `"${v.replace(/"/g, '""')}"`;
        const header = 'name,description,statementOfTruth,negativeInteractionCount,positiveInteractionCount';
        const lines = rows.map(r => [
            escape(r.name),
            escape(r.description),
            escape(r.statementOfTruth),
            r.negativeInteractionCount.toString(),
            r.positiveInteractionCount.toString(),
        ].join(','));
        triggerDownload([header, ...lines].join('\n'), 'fault-tree-nodes.csv', 'text/csv;charset=utf-8;');
        setDownloadAnchorEl(null);
    };

    const handleDownloadJson = (): void => {
        triggerDownload(JSON.stringify(buildDownloadRows(), null, 2), 'fault-tree-nodes.json', 'application/json');
        setDownloadAnchorEl(null);
    };

    // Process data into tree structure
    const treeData = useMemo(() => {
        // Get the initiating event's fault tree
        const initiatingEvent = eventSequenceData?.events?.[0];
        const faultTree = initiatingEvent?.faultTree;
        const faultTreeNodes: FaultTreeNode[] = faultTree?.faultTreeNodes || [];

        if (faultTreeNodes.length === 0) return null;

        // Build a map of fault tree nodes by ID
        // Track both failures (negative interactions) and barrier successes (positive interactions)
        const nodeMap = new Map<number, FaultTreeNode & {
            children?: any[],
            mappedReportCount?: number,
            failureCount?: number,
            barrierSuccessCount?: number,
            unmappedChildCount?: number,
            isVirtual?: boolean
        }>();
        faultTreeNodes.forEach(node => {
            nodeMap.set(node.id, {
                ...node,
                children: [],
                mappedReportCount: 0,
                failureCount: 0,
                barrierSuccessCount: 0,
                unmappedChildCount: 0,
            });
        });

        // Apply badge counts from BFF overview data (negativeInteractionCount = red, positiveInteractionCount = green)
        const badgeMap = new Map((nodeBadges ?? []).map(b => [b.nodeId, b]));
        nodeMap.forEach((node, nodeId) => {
            const badge = badgeMap.get(nodeId);
            if (badge) {
                node.failureCount = badge.negativeInteractionCount;
                node.barrierSuccessCount = badge.positiveInteractionCount;
                node.mappedReportCount = badge.negativeInteractionCount + badge.positiveInteractionCount;
                node.unmappedChildCount = badge.unmappedChildCount ?? 0;
            }
        });

        // Build the tree structure using childIds
        let rootNode: any = null;
        nodeMap.forEach((node) => {
            if (node.childIds && node.childIds.length > 0) {
                node.childIds.forEach(childId => {
                    const childNode = nodeMap.get(childId);
                    if (childNode) {
                        node.children!.push(childNode);
                    }
                });
            }

            // Find root (node without parent or parent not in map)
            if (!node.parentId || !nodeMap.has(node.parentId)) {
                rootNode = node;
            }
        });

        // If no explicit root found, use the first node
        if (!rootNode && nodeMap.size > 0) {
            rootNode = Array.from(nodeMap.values())[0];
        }

        // Add virtual "not mapped to child" nodes where parent count exceeds sum of children.
        // This makes visible the reports that reached a node but had no child selected (e.g.
        // MAPPED_INCOMPLETE reports at an OR gate where the LLM could not pick a child).
        const addVirtualNodes = (node: any): void => {
            if (!node.children || node.children.length === 0 || node.isVirtual) return;
            node.children.forEach((child: any) => addVirtualNodes(child));

            const unmapped: number = node.unmappedChildCount || 0;
            if (unmapped > 0) {
                node.children.push({
                    id: -(node.id as number),
                    name: `Unable to assign ${unmapped} reports to a child`,
                    isVirtual: true,
                    failureCount: unmapped,
                    barrierSuccessCount: 0,
                    mappedReportCount: unmapped,
                    children: [],
                    nodeClass: null,
                    type: null,
                    parentId: node.id,
                    childIds: [],
                });
            }
        };
        if (rootNode) addVirtualNodes(rootNode);

        return rootNode;
    }, [nodeBadges, eventSequenceData]);

    // Handle container resize
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setDimensions({ width, height });
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Render D3 Dendrogram
    useEffect(() => {
        if (!svgRef.current || !treeData || dimensions.width === 0 || dimensions.height === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous render

        // Use container dimensions
        const width = dimensions.width;
        const height = dimensions.height;
        const margin = { top: 20, right: 120, bottom: 20, left: 80 };

        svg.attr('width', width).attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .style('width', '100%')
            .style('height', '100%');

        // Position based on orientation
        const xOffset = orientation === 'ltr' ? margin.left : width - margin.right;
        const g = svg.append('g')
            .attr('transform', `translate(${xOffset},${height / 2})`);

        // Wrap text into tspans. When `maxLines` is set, the last allowed line is trimmed
        // with an ellipsis and the function returns true so the caller can attach a
        // <title> tooltip with the full text. Capping the line count keeps a node's label
        // from flowing downward into the count badges of the node beneath it.
        const wrapText = (
            textElement: any,
            text: string,
            maxWidth: number,
            centerVertically: boolean = true,
            maxLines: number = Infinity,
        ): boolean => {
            const words: string[] = text.split(/\s+/);
            const lineHeight = 1.1; // ems
            const y = textElement.attr('y') || 0;
            const dy = parseFloat(textElement.attr('dy') || '0');

            textElement.text(''); // Clear existing text

            let line: string[] = [];
            let lineNumber = 0;
            let truncated = false;
            let tspan = textElement.append('tspan')
                .attr('x', textElement.attr('x'))
                .attr('y', y)
                .attr('dy', dy + 'em');

            for (const word of words) {
                line.push(word);
                tspan.text(line.join(' '));

                const node = tspan.node();
                if (node && node.getComputedTextLength() > maxWidth) {
                    line.pop();
                    tspan.text(line.join(' '));

                    if (lineNumber + 1 >= maxLines) {
                        // Out of allowed lines — trim this line so an ellipsis fits, then stop.
                        truncated = true;
                        const trimmed: string[] = line.slice();
                        trimmed.push('…');
                        tspan.text(trimmed.join(' '));
                        let t = tspan.node();
                        while (trimmed.length > 1 && t && t.getComputedTextLength() > maxWidth) {
                            trimmed.splice(trimmed.length - 2, 1); // drop the word before the ellipsis
                            tspan.text(trimmed.join(' '));
                            t = tspan.node();
                        }
                        break;
                    }

                    line = [word];
                    tspan = textElement.append('tspan')
                        .attr('x', textElement.attr('x'))
                        .attr('y', y)
                        .attr('dy', ++lineNumber * lineHeight + dy + 'em')
                        .text(word);
                }
            }

            // Center the text block vertically (default), or top-anchor it (offset 0)
            // so a badge-bearing node's name flows downward and never rises into the badges.
            const totalLines = lineNumber + 1;
            const offset = centerVertically ? -(totalLines - 1) * lineHeight * 0.5 : 0;
            textElement.selectAll('tspan').attr('dy', function(this: any, _d: any, i: number) {
                return (i * lineHeight + offset + dy) + 'em';
            });

            return truncated;
        };

        // Function to collapse nodes beyond a certain depth
        // depth 0 = root, depth 1 = first level, depth 2 = second level, etc.
        // So maxDepth=2 means show levels 0, 1, 2 (total of 3 visible levels)
        const collapseAfterDepth = (d: TreeNode, maxDepth: number): void => {
            // Collapse nodes at or beyond maxDepth that have children
            if (d.depth >= maxDepth && d.children) {
                d._children = d.children;
                d.children = undefined;
            } else if (d.children) {
                // Recursively process children if not collapsed
                d.children.forEach(child => collapseAfterDepth(child as TreeNode, maxDepth));
            }
        };

        // Toggle children on click - only expand one level at a time
        const toggleChildren = (event: any, d: TreeNode): void => {
            if (d.children) {
                // Collapse: hide children
                d._children = d.children;
                d.children = undefined;
            } else if (d._children) {
                // Expand: show immediate children only
                d.children = d._children;
                d._children = undefined;

                // Ensure newly revealed children start collapsed if they have children
                if (d.children) {
                    d.children.forEach((child: any) => {
                        const childNode = child as TreeNode;
                        if (childNode.children && childNode.children.length > 0) {
                            childNode._children = childNode.children;
                            childNode.children = undefined;
                        }
                    });
                }
            }
            // Persist collapse state so it survives tab switches and fullscreen toggling
            const collapsedIds = new Set<number>();
            const walkCollapsed = (n: TreeNode): void => {
                if (n._children) collapsedIds.add(n.data.id);
                (n.children || []).forEach(child => walkCollapsed(child as TreeNode));
            };
            if (rootNodeRef.current) walkCollapsed(rootNodeRef.current);
            setCollapsedNodeIds(collapsedIds);
            setUpdateTrigger(prev => prev + 1); // Force re-render
        };

        // Create tree layout with fixed horizontal spacing
        const treeLayout = d3.tree<FaultTreeNode & { mappedReportCount?: number }>()
            .nodeSize([55, 350]); // [vertical spacing of 55px (increased for thicker lines), horizontal spacing of 350px]

        // Create hierarchy
        const root = d3.hierarchy(treeData) as TreeNode;

        // Restore expand/collapse state from the store so it survives tab switches and fullscreen.
        // storedCollapsedIds is null only on the very first render for this IRP.
        const storedCollapsedIds = getCollapsedNodeIds();
        if (storedCollapsedIds === null) {
            collapseAfterDepth(root, 2); // Default: show 3 levels (depth 0–2)
            const initialCollapsed = new Set<number>();
            const collectCollapsed = (n: TreeNode): void => {
                if (n._children) initialCollapsed.add(n.data.id);
                (n.children || []).forEach(child => collectCollapsed(child as TreeNode));
            };
            collectCollapsed(root);
            setCollapsedNodeIds(initialCollapsed);
        } else {
            // Build a complete node map before making any modifications so that collapsing
            // a parent does not prevent its children from being found and collapsed too.
            const allNodes = new Map<number, TreeNode>();
            root.eachBefore((n) => allNodes.set(n.data.id, n as TreeNode));
            storedCollapsedIds.forEach(id => {
                const n = allNodes.get(id);
                if (n?.children) {
                    (n as TreeNode)._children = n.children;
                    n.children = undefined;
                }
            });
        }
        rootNodeRef.current = root;

        const treeNodes = treeLayout(root);

        // Coordinate transform based on orientation
        const xPos = (d: any): number => orientation === 'ltr' ? d.y : -d.y;

        // Calculate max score for scaling line widths
        let maxScore = 0;
        if (scaleLineWidth) {
            treeNodes.descendants().forEach(node => {
                const mappedCount = (node.data as any).mappedReportCount || 0;
                if (mappedCount > maxScore) {
                    maxScore = mappedCount;
                }
            });
        }

        // Function to calculate line width based on target node's score
        const getLineWidth = (link: any): number => {
            if (!scaleLineWidth || maxScore === 0) {
                return 1.5; // Default width
            }
            const targetScore = (link.target.data as any).mappedReportCount || 0;
            // Scale from 1.5 (min) to 50 (max) based on score
            return 1.5 + (targetScore / maxScore) * (50 - 1.5);
        };

        // Scale stroke color: solid red at max, fading via alpha toward min
        const getLineColor = (link: any): string => {
            if (!scaleLineWidth || maxScore === 0) {
                return theme.palette.divider;
            }
            const t = ((link.target.data as any).mappedReportCount || 0) / maxScore;
            const alpha = (0.2 + t * 0.6).toFixed(2);
            return `rgba(211, 47, 47, ${alpha})`;
        };

        // Draw links
        const links = treeNodes.links();
        g.selectAll('.link')
            .data(links)
            .enter().append('path')
            .attr('class', 'link')
            .attr('d', d3.linkHorizontal<any, any>()
                .x(d => xPos(d))
                .y(d => d.x))
            .attr('fill', 'none')
            .attr('stroke', d => getLineColor(d))
            .attr('stroke-width', d => getLineWidth(d));

        // Draw nodes
        const node = g.selectAll('.node')
            .data(treeNodes.descendants())
            .enter().append('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${xPos(d)},${d.x})`)
            .style('cursor', d => (d.data as any).isVirtual ? 'default' : 'pointer')
            .on('click', (event, d) => { if (!(d.data as any).isVirtual) toggleChildren(event, d as TreeNode); });

        // Add shapes for nodes - squares for barriers, circles for precursors, diamonds for virtual
        node.each(function(d) {
            const isVirtual = (d.data as any).isVirtual === true;
            const nodeClass = d.data.nodeClass;
            const nodeType = d.data.type;
            const element = d3.select(this);

            if (isVirtual) {
                // Diamond shape for virtual "not mapped to child" nodes
                element.append('polygon')
                    .attr('points', '0,-10 10,0 0,10 -10,0')
                    .attr('fill', theme.palette.grey[200])
                    .attr('stroke', theme.palette.grey[500])
                    .attr('stroke-width', 1.5)
                    .attr('stroke-dasharray', '3,2');
                return;
            }

            let fillColor: string;
            if (nodeClass === 'PRECURSOR') {
                fillColor = '#FFD700';
            } else if (nodeClass === 'BARRIER') {
                fillColor = '#4CAF50';
            } else {
                fillColor = theme.palette.grey[400];
            }

            if (nodeClass === 'BARRIER') {
                element.append('rect')
                    .attr('x', -8)
                    .attr('y', -16)
                    .attr('width', 16)
                    .attr('height', 32)
                    .attr('fill', fillColor)
                    .attr('stroke', theme.palette.divider)
                    .attr('stroke-width', 1.5);
            } else {
                element.append('circle')
                    .attr('r', 12)
                    .attr('fill', fillColor)
                    .attr('stroke', theme.palette.divider)
                    .attr('stroke-width', 1.5);
            }

            if (nodeType === 'AND' || nodeType === 'OR') {
                const gateOffset = orientation === 'ltr' ? 25 : -25;
                const gateGroup = element.append('g')
                    .attr('transform', `translate(${gateOffset}, 0)`);

                gateGroup.append('circle')
                    .attr('r', 10)
                    .attr('fill', 'white')
                    .attr('stroke', theme.palette.divider)
                    .attr('stroke-width', 1);

                gateGroup.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', '0.35em')
                    .attr('font-size', '7px')
                    .attr('font-weight', 'bold')
                    .attr('fill', theme.palette.text.primary)
                    .text(nodeType);
            }
        });

        // Add labels with text wrapping. Nodes that carry report-count badges (drawn just
        // above the node at y=-20..-4) get their label top-anchored a little lower so a
        // multi-line name flows downward and never rises up into the badges.
        const hasBadge = (d: any): boolean =>
            !(d.data as any).isVirtual
            && ((((d.data as any).failureCount || 0) > 0) || (((d.data as any).barrierSuccessCount || 0) > 0));
        node.append('text')
            .attr('dy', d => hasBadge(d) ? '0.9em' : '0.35em')
            .attr('x', d => {
                const isVirtual = (d.data as any).isVirtual === true;
                const hasChildren = !!d.children;
                const nodeType = d.data.type;
                const hasGate = nodeType === 'AND' || nodeType === 'OR';

                if (isVirtual) return orientation === 'ltr' ? 15 : -15;

                if (orientation === 'ltr') {
                    if (hasChildren) return -15;
                    if (hasGate) return 40;
                    return 15;
                } else {
                    if (hasChildren) return 15;
                    if (hasGate) return -40;
                    return -15;
                }
            })
            .attr('text-anchor', d => {
                const isVirtual = (d.data as any).isVirtual === true;
                const hasChildren = !!d.children;
                if (isVirtual) return orientation === 'ltr' ? 'start' : 'end';
                if (orientation === 'ltr') {
                    return hasChildren ? 'end' : 'start';
                } else {
                    return hasChildren ? 'start' : 'end';
                }
            })
            .style('font-size', d => (d.data as any).isVirtual ? '11px' : '12px')
            .style('fill', d => (d.data as any).isVirtual ? theme.palette.text.disabled : theme.palette.text.primary)
            .style('font-style', d => (d.data as any).isVirtual ? 'italic' : 'normal')
            .style('pointer-events', 'none')
            .each(function(d) {
                const text = d3.select(this);
                const name: string = d.data.name || (d.data as any).uniqueId;
                const badged: boolean = hasBadge(d);
                // Badge nodes flow their label downward, so cap them at 2 lines to stay
                // clear of the next node's badges; centered (non-badge) labels get more.
                const truncated: boolean = wrapText(text, name, 250, !badged, badged ? 2 : 4);
                if (truncated) {
                    // Show the full name on hover. Enabling pointer-events lets the native
                    // <title> tooltip fire; clicks still bubble to the node's toggle handler.
                    text.append('title').text(name);
                    text.style('pointer-events', 'auto');
                }
            });

        // Add report count badges — real nodes only (virtual nodes show count in their label)
        const badgeWidth = (count: number): number =>
            Math.max(20, String(count).length * 7 + 8);

        // Failure badge (red)
        const failureBadges = node.filter(d => (d.data as any).failureCount > 0 && !(d.data as any).isVirtual);
        const failureBadgeGroups = failureBadges.append('g')
            .attr('class', 'failure-badge');

        failureBadgeGroups.append('rect')
            .attr('x', d => {
                const fw = badgeWidth((d.data as any).failureCount);
                return d.children ? -(15 + fw) : 15;
            })
            .attr('y', -20)
            .attr('width', d => badgeWidth((d.data as any).failureCount))
            .attr('height', 16)
            .attr('rx', 3)
            .attr('fill', theme.palette.error.main);

        failureBadgeGroups.append('text')
            .attr('x', d => {
                const fw = badgeWidth((d.data as any).failureCount);
                return d.children ? -(15 + fw / 2) : 15 + fw / 2;
            })
            .attr('y', -8)
            .attr('text-anchor', 'middle')
            .attr('fill', 'white')
            .style('font-size', '10px')
            .style('font-weight', 'bold')
            .text(d => (d.data as any).failureCount);

        failureBadgeGroups.append('title')
            .text(d => `Failures/Incident Paths: ${(d.data as any).failureCount || 0}`);

        // Barrier success badge (green)
        const successBadges = node.filter(d => (d.data as any).barrierSuccessCount > 0 && !(d.data as any).isVirtual);
        const successBadgeGroups = successBadges.append('g')
            .attr('class', 'success-badge');

        successBadgeGroups.append('rect')
            .attr('x', d => {
                const fw = (d.data as any).failureCount > 0
                    ? badgeWidth((d.data as any).failureCount) + 5
                    : 0;
                const sw = badgeWidth((d.data as any).barrierSuccessCount);
                return d.children ? -(15 + fw + sw) : 15 + fw;
            })
            .attr('y', -20)
            .attr('width', d => badgeWidth((d.data as any).barrierSuccessCount))
            .attr('height', 16)
            .attr('rx', 3)
            .attr('fill', theme.palette.success.main);

        successBadgeGroups.append('text')
            .attr('x', d => {
                const fw = (d.data as any).failureCount > 0
                    ? badgeWidth((d.data as any).failureCount) + 5
                    : 0;
                const sw = badgeWidth((d.data as any).barrierSuccessCount);
                return d.children ? -(15 + fw + sw / 2) : 15 + fw + sw / 2;
            })
            .attr('y', -8)
            .attr('text-anchor', 'middle')
            .attr('fill', 'white')
            .style('font-size', '10px')
            .style('font-weight', 'bold')
            .text(d => (d.data as any).barrierSuccessCount);

        successBadgeGroups.append('title')
            .text(d => `Barrier Successes: ${(d.data as any).barrierSuccessCount || 0}`);

        // Add expand/collapse indicator (plus/minus symbol) — real nodes only
        node.filter(d => {
            const treeNode = d as TreeNode;
            return !!(treeNode.children || treeNode._children) && !(d.data as any).isVirtual;
        })
            .append('text')
            .attr('x', 0)  // Center horizontally
            .attr('y', 4)  // Center vertically with slight adjustment for text baseline
            .style('text-anchor', 'middle')
            .style('font-size', '16px')
            .style('font-weight', 'bold')
            .style('fill', 'white')  // White text to contrast with node colors
            .style('pointer-events', 'none')
            .text(d => (d as TreeNode)._children ? '+' : '-');

        // Add tooltips
        node.append('title')
            .text(d => {
                if ((d.data as any).isVirtual) {
                    return `Unable to assign ${(d.data as any).failureCount} reports to a child node.\nThese reports matched the parent node but the AI agent was unable to determine an appropriate child node. Either there is insufficient data in the report, or the model has a gap.`;
                }
                const failureCount = (d.data as any).failureCount || 0;
                const barrierSuccessCount = (d.data as any).barrierSuccessCount || 0;
                const totalCount = failureCount + barrierSuccessCount;
                const nodeType = d.data.type || 'UNKNOWN';
                const hasChildren = (d as TreeNode).children || (d as TreeNode)._children;
                const expandHint = hasChildren ? '\n(Click to expand/collapse)' : '';

                let mappingDetails = '';
                if (totalCount > 0) {
                    mappingDetails = `\nTotal Mapped Reports: ${totalCount}`;
                    if (failureCount > 0) mappingDetails += `\n  Failures/Incident Paths: ${failureCount}`;
                    if (barrierSuccessCount > 0) mappingDetails += `\n  Barrier Successes: ${barrierSuccessCount}`;
                } else {
                    mappingDetails = '\nMapped Reports: 0';
                }

                const nodeDetail = d.data.description || d.data.statementOfTruth;
                const detailText = nodeDetail ? `\n\n${nodeDetail}` : '';
                return `${d.data.name}\nType: ${nodeType}${detailText}${mappingDetails}${expandHint}`;
            });

        // Add eye icon for navigation to Precursor/Barrier Review (real nodes only)
        const eyeIconSize = 14;
        const eyeIconYOffset = 18;

        // Only drawn when the host can act on it: a shakedown has no Precursor/Control Review.
        const eyeIconGroup = node.filter(d => onNavigateToNode != null && !(d.data as any).isVirtual).append('g')
            .attr('class', 'eye-icon')
            .attr('transform', `translate(0, ${eyeIconYOffset})`)
            .style('cursor', 'pointer')
            .style('opacity', 0.4)
            .style('pointer-events', 'all')
            .on('click', function(event, d) {
                event.stopPropagation(); // Prevent node expand/collapse
                const nodeId = d.data.id;
                if (nodeId !== undefined) {
                    onNavigateToNode?.(nodeId);
                }
            })
            .on('mouseover', function() {
                d3.select(this).transition().duration(200).style('opacity', 1.0);
            })
            .on('mouseout', function() {
                d3.select(this).transition().duration(200).style('opacity', 0.4);
            });

        // Add the eye icon SVG path (scaled to fit)
        eyeIconGroup.append('path')
            .attr('d', EYE_ICON_PATH)
            .attr('transform', `translate(${-eyeIconSize / 2}, ${-eyeIconSize / 2}) scale(${eyeIconSize / 24})`)
            .attr('fill', theme.palette.primary.main);

        // Add tooltip to eye icon
        eyeIconGroup.append('title')
            .text('View node details in Precursor/Control Review');

        // Add zoom and pan behavior
        const storedTransform = getTransform();
        const zoom = d3.zoom()
            .scaleExtent([0.3, 3])
            .on('zoom', (event) => {
                g.attr('transform', event.transform);
                currentTransformRef.current = event.transform;
            })
            .on('end', (event) => {
                // Persist on zoom-end so the position survives tab switches and fullscreen
                setTransform({ x: event.transform.x, y: event.transform.y, k: event.transform.k });
            });

        svg.call(zoom as any);

        // currentTransformRef is reset to null on remount; fall back to the store value in that case.
        const transformToRestore = currentTransformRef.current
            ?? (storedTransform
                ? d3.zoomIdentity.translate(storedTransform.x, storedTransform.y).scale(storedTransform.k)
                : null);

        if (transformToRestore) {
            svg.call(zoom.transform as any, transformToRestore);
            currentTransformRef.current = transformToRestore;
        } else {
            const initialTransform = d3.zoomIdentity.translate(xOffset, height / 2).scale(1);
            svg.call(zoom.transform as any, initialTransform);
            currentTransformRef.current = initialTransform;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData, theme, updateTrigger, dimensions, orientation, scaleLineWidth]);

    // Calculate statistics
    const stats = useMemo(() => {
        if (!treeData) return { totalNodes: 0, mappedNodes: 0, totalMappings: 0 };

        let totalNodes = 0;
        let mappedNodes = 0;
        let totalMappings = 0;

        const countNodes = (node: any): void => {
            if (node.isVirtual) return;
            totalNodes++;
            if (node.mappedReportCount > 0) {
                mappedNodes++;
                totalMappings += node.mappedReportCount;
            }
            if (node.children) {
                node.children.forEach(countNodes);
            }
        };

        countNodes(treeData);

        return { totalNodes, mappedNodes, totalMappings };
    }, [treeData]);

    return (
        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Subtle progress bar at the top while re-fetching badges (stale-while-revalidate) */}
            {isLoading && nodeBadges !== undefined && (
                <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, borderRadius: '4px 4px 0 0' }} />
            )}
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`Switch to ${orientation === 'ltr' ? 'right-to-left' : 'left-to-right'} layout`}>
                            <IconButton
                                size="small"
                                onClick={(): void => setOrientation(prev => prev === 'ltr' ? 'rtl' : 'ltr')}
                                sx={{
                                    backgroundColor: 'action.hover',
                                    '&:hover': {
                                        backgroundColor: 'action.selected'
                                    }
                                }}
                            >
                                <SwapHoriz fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={`${scaleLineWidth ? 'Disable' : 'Enable'} scaled line widths based on report counts`}>
                            <IconButton
                                size="small"
                                onClick={(): void => setScaleLineWidth(prev => !prev)}
                                sx={{
                                    backgroundColor: scaleLineWidth ? 'primary.main' : 'action.hover',
                                    color: scaleLineWidth ? 'primary.contrastText' : 'text.primary',
                                    '&:hover': {
                                        backgroundColor: scaleLineWidth ? 'primary.dark' : 'action.selected'
                                    }
                                }}
                            >
                                <LineWeight fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Download node data (CSV or JSON)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={(e): void => setDownloadAnchorEl(e.currentTarget)}
                                    disabled={!eventSequenceData}
                                    sx={{
                                        backgroundColor: 'action.hover',
                                        '&:hover': { backgroundColor: 'action.selected' },
                                    }}
                                >
                                    <Download fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Menu
                            anchorEl={downloadAnchorEl}
                            open={Boolean(downloadAnchorEl)}
                            onClose={(): void => setDownloadAnchorEl(null)}
                        >
                            <MenuItem onClick={handleDownloadCsv}>Download CSV</MenuItem>
                            <MenuItem onClick={handleDownloadJson}>Download JSON</MenuItem>
                        </Menu>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            Fault Tree Structure
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Total number of nodes in the fault tree structure.">
                            <Chip
                                label={`${stats.totalNodes} nodes`}
                                size="small"
                                variant="outlined"
                            />
                        </Tooltip>
                        <Tooltip title="Number of fault tree nodes that have at least one report mapped to them under the current filter.">
                            <Chip
                                label={`${stats.mappedNodes} nodes have reports`}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        </Tooltip>
                        <Tooltip title="Total number of individual report-to-node mappings across the entire result set. One report can contribute multiple mappings if it maps to several nodes.">
                            <Chip
                                label={`${stats.totalMappings} mappings`}
                                size="small"
                                color="info"
                            />
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Dendrogram */}
                <Box
                    ref={containerRef}
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        backgroundColor: theme.palette.grey[50],
                        position: 'relative',
                        minHeight: 400
                    }}
                >
                    {eventSequenceData === undefined ? (
                        /* Tree structure not yet loaded — show spinner */
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary">
                                Loading fault tree...
                            </Typography>
                        </Box>
                    ) : treeData ? (
                        <svg
                            ref={svgRef}
                            style={{
                                display: 'block',
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: 0,
                                left: 0
                            }}
                        />
                    ) : (
                        <Box sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                                No fault tree data available
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Legend */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: '#FFD700',
                            border: `1px solid ${theme.palette.divider}`
                        }} />
                        <Typography variant="caption">Precursor</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 8,
                            height: 16,
                            backgroundColor: '#4CAF50',
                            border: `1px solid ${theme.palette.divider}`
                        }} />
                        <Typography variant="caption">Barrier</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 12, borderRadius: '3px', backgroundColor: 'error.main' }} />
                        <Typography variant="caption">Failures (red badge)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 12, borderRadius: '3px', backgroundColor: 'success.main' }} />
                        <Typography variant="caption">Barrier successes (green badge)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                            width: 12,
                            height: 12,
                            transform: 'rotate(45deg)',
                            backgroundColor: theme.palette.grey[200],
                            border: `1.5px dashed ${theme.palette.grey[500]}`
                        }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>Unable to assign to a child</Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

/**
 * The workbook's dendrogram: the view above, wired to the workbook store for the model it draws,
 * where a node click goes, and the collapse and pan state that survive tab switches.
 */
const FaultTreeDendrogram: React.FC<FaultTreeDendrogramProps> = (props) => {
    const eventSequenceData = useIRPWorkbookStore((state) => state.eventSequenceData);
    const navigateToPrecursorBarrier = useIRPWorkbookStore((state) => state.navigateToPrecursorBarrier);
    const setDendrogramCollapsedNodeIds = useIRPWorkbookStore((state) => state.setDendrogramCollapsedNodeIds);
    const setDendrogramTransform = useIRPWorkbookStore((state) => state.setDendrogramTransform);
    const storeApi = useIRPWorkbookStoreApi();

    return (
        <FaultTreeDendrogramView
            {...props}
            eventSequenceData={eventSequenceData ?? null}
            onNavigateToNode={navigateToPrecursorBarrier}
            getCollapsedNodeIds={(): Set<number> | null => storeApi.getState().dendrogramCollapsedNodeIds}
            setCollapsedNodeIds={setDendrogramCollapsedNodeIds}
            getTransform={(): { x: number; y: number; k: number } | null => storeApi.getState().dendrogramTransform}
            setTransform={setDendrogramTransform}
        />
    );
};

export default FaultTreeDendrogram;